import { makeObservable, action, observable, computed } from "mobx";
export interface IPaginationStore<TDataSource> {
	pageSize: number;
	sourceData: Array<TDataSource>;
	hasMultiplePages: boolean;
	changePage: (newPage: number) => void;
}

export interface IPaginationStoreInit<TDataSource> {
	sourceData: TDataSource;
}
/**
 * Provides base class for generic pagination
 */
export abstract class PaginationStore<TDataSource> implements IPaginationStore<TDataSource> {
	sourceData: Array<TDataSource> = this.getDefaultRecentResults();
	@observable pageSize: number = 10;
	itemOffset: number = 0;
	pageCount: number = 0;
	topResults: number = 10;

	constructor() {
		makeObservable(this, undefined, {
			autoBind: true,
		});
	}

	/**
	 * The default (empty) source
	 */
	protected abstract getDefaultRecentResults(): Array<TDataSource>;

	/**
	 * Returns boolean if data source has multiple pages
	 */
	@computed get hasMultiplePages() {
		return this.hasMultiplePagesImpl();
	}

	/**
	 * If there are multiple results, call this to advance to a specific page of results (0 index based)
	 * @param newPage The page number you wish to show results for.
	 */
	@action.bound changePage(newPage: number) {
		if (this.sourceData && this.sourceData.length > 0) {
			this.itemOffset = (newPage * this.pageSize) % this.sourceData.length;
			const endOffset = this.itemOffset + this.pageSize;

			this.sourceData = this.sourceData.slice(this.itemOffset, endOffset);
			this.pageCount = Math.ceil(this.sourceData.length / this.pageSize);
			this.topResults *= this.pageCount;
		}
	}

	/**
	 * Determines if data source has multiple pages
	 * @returns whether data source contains multiple pages
	 */
	hasMultiplePagesImpl(): boolean {
		if (this.sourceData && this.sourceData.length > 0) {
			return this.sourceData.length > this.topResults;
		}
		return false;
	}
}
